import * as PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import StarRating from '../../utils/starRating';
import { useClickAway } from 'react-use';
import {
  likeAMessage,
  updateMessage,
  setMessageDeletionState,
  setSelectedMessage
} from '../../../../redux/slices/communication';
import { useDispatch, useSelector } from 'react-redux';
import { useLocales } from '../../../../locales';
import MessageText from './messageText';
import MessageToolBar from './messageToolbar';
import { T, useTranslate } from '@tolgee/react';
import { shouldShowToolbar, getAvailableActions } from './messageRules';

export default function MessageContent({ data, left, rules }) {
  const {
    components: { quillEditor }
  } = useSelector((state) => state.communication);
  const onReplyClick = (message) => {
    console.log('reply', message);
    // add msg to quill
    const username = '@' + message.social_media_platform_user.name;
    // check if username in quill
    const quillText = quillEditor.getText();
    if (quillText.includes(username)) {
      return;
    }
    // delete all text
    quillEditor.deleteText(0, quillText.length);
    // add username to quill
    quillEditor.insertText(
      0,
      '@' + message.social_media_platform_user.name + ' '
    );
  };

  const likeComment = (data) => {
    dispatch(likeAMessage(data.uuid, activeConversation.uuid, () => {}));
    setLikedByMe(!likedByMe);
  };
  const [likedByMe, setLikedByMe] = useState(data?.is_liked_by_me);
  useEffect(() => {
    setLikedByMe(data?.is_liked_by_me);
  }, [data?.is_liked_by_me]);
  const { currentLang } = useLocales();
  const [msg, setMsg] = useState(
    JSON.parse(data.message_content_quill || '{}')
  );

  const submitEditedMessage = () => {
    dispatch(
      updateMessage(
        JSON.stringify(msg),
        data.uuid,
        activeConversation.uuid,
        () => {}
      )
    );
    setEditMessage(false);
  };
  const deleteMessageBtn = (data) => {
    dispatch(setSelectedMessage(data));
    dispatch(setMessageDeletionState(true));
  };
  const [editMessage, setEditMessage] = useState(false);
  const {
    data: { activeConversation }
  } = useSelector((state) => state.communication);
  const dispatch = useDispatch();

  const { t } = useTranslate();

  const getMessageContent = () => {
    const emptyRatingMessage = t(
      'communication.messageText.rating_message_empty'
    );
    const content = data?.message_content_html
      ? data?.message_content_html
      : data?.message_content;
    if (!content && data.rating !== null && data.rating !== undefined) {
      return { status: 'empty', text: emptyRatingMessage };
    }
    return { status: 'message', text: content };
  };
  const [messageContent, setMessageContent] = useState(getMessageContent());
  useEffect(() => {
    setMessageContent(getMessageContent());
  }, [data]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  useClickAway(dropdownRef, () => {
    setShowDropdown(false);
  });
  const [showToolbar, setShowToolbar] = useState(false);
  const [availableActions, setAvailableActions] = useState([]);

  useEffect(() => {
    const shouldShow = shouldShowToolbar(rules, data);
    setShowToolbar(shouldShow);
    
    if (shouldShow) {
      const actions = getAvailableActions(rules, data);
      setAvailableActions(actions);
    }
  }, [rules, data]);

  return (
    <div>
      <div
        className={`px-5 rounded ${
          data?.is_internal_note
            ? 'bg-light-warning'
            : data.is_sent_by_me
            ? 'bg-light-primary'
            : 'bg-light-info'
        } pb-5`}
        style={{
          marginLeft: left ? '45px' : undefined, // Added 'px' for margin value
          marginRight: left ? undefined : '45px', // Added 'px' for margin value
          position: 'relative',
          minWidth: 100, // TODO: replace px value here!
          opacity: data?.is_hidden_on_platform ? 0.5 : 1,
          paddingTop: data?.is_hidden_on_platform ? '35px' : '20px'
        }}
      >
        {data?.is_hidden_on_platform && (
          <div className="position-absolute top-0 start-0 mt-2 ms-2">
            <span className="badge badge-light-danger badge-sm">
              <i className="fa-duotone fa-eye-slash me-1" />
              {t('communication.messages.hidden')}
            </span>
          </div>
        )}
        {data.rating !== null && data.rating !== undefined && (
          <div className="position-absolute top-0 end-0 mt-n6">
            <StarRating rating={data.rating} />
          </div>
        )}
        <MessageText
          locationUUID={activeConversation?.location_uuid}
          isInternalNote={data.is_internal_note}
          messageContent={messageContent}
          left={left}
          editMessage={editMessage}
          setMessageContent={setMessageContent}
          setMsg={setMsg}
          msgDelta={msg}
          hasError={data.has_error}
          additional_data={data.additional_data}
        />
      </div>
      <div
        className="d-flex justify-content-between"
        style={{
          marginLeft: left ? 50 : undefined
        }}
      >
        {activeConversation?.rules?.can_view_likes &&
        !data?.is_internal_note &&
        !data?.is_sent_by_me ? (
          <div className="d-flex align-items-center justify-content-center me-2">
            <span>
              <i
                className={`${
                  likedByMe ? 'fa-solid' : 'fa-regular'
                } fa-heart text-danger fs-base ${
                  activeConversation?.rules?.can_like_messages
                    ? 'cursor-pointer'
                    : ''
                }`}
                onClick={() => likeComment(data)}
                style={{
                  verticalAlign: 'middle',
                  display: 'inline-block'
                }}
              ></i>
            </span>
            {false && (
              <span className="ms-2 small">
                {new Intl.NumberFormat(currentLang, {
                  notation: 'compact',
                  compactDisplay: 'short'
                }).format(data.total_reactions_count)}
              </span>
            )}
          </div>
        ) : (
          <div></div>
        )}
        <div
          className="d-flex align-items-center"
          style={{
            marginLeft: left ? 45 : undefined,
            marginRight: left ? undefined : 45
          }}
        >
          {activeConversation.platform === 'instagram' &&
            activeConversation.type === 'comment' && (
              <div className="pt-3 me-4">
                <span
                  className="fw-bold text-muted cursor-pointer"
                  onClick={() => onReplyClick(data)}
                >
                  <T keyName={'communication.messages.reply'} />
                </span>
              </div>
            )}

          {showToolbar && (
            <MessageToolBar
              left={left}
              editMessage={editMessage}
              setShowDropdown={setShowDropdown}
              showDropdown={showDropdown}
              dropdownRef={dropdownRef}
              data={data}
              setEditMessage={setEditMessage}
              submitEditedMessage={submitEditedMessage}
              deleteMessageBtn={deleteMessageBtn}
              availableActions={availableActions}
            />
          )}
        </div>
      </div>
    </div>
  );
}

MessageContent.propTypes = {
  data: PropTypes.object.isRequired,
  left: PropTypes.bool.isRequired,
  rules: PropTypes.object
};
